import { useEffect, useState } from "react";
import { Calendar, Clock, Video, MapPin, DollarSign, CheckCircle2, ChevronLeft, ChevronRight, Loader2, Globe } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";
import { PageMeta } from "@/components/PageMeta";

type MeetingTypePublic = {
  id: number;
  title: string;
  description?: string | null;
  duration: number;
  price?: number | null;
  color?: string | null;
  location?: string | null;
};

type Slot = {
  start: string;
  end: string;
  available: boolean;
};

type Step = "select-date" | "select-time" | "fill-form" | "confirmed";

function groupSlotsByDate(slots: Slot[]): Record<string, Slot[]> {
  const grouped: Record<string, Slot[]> = {};
  for (const slot of slots) {
    const date = slot.start.slice(0, 10);
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(slot);
  }
  return grouped;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function BookingPage({ slug }: { slug: string }) {
  const [meetingType, setMeetingType] = useState<MeetingTypePublic | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>("select-date");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [meetingLink, setMeetingLink] = useState<string | null>(null);

  const [dateOffset, setDateOffset] = useState(0);

  useEffect(() => {
    async function load() {
      try {
        const resp = await apiFetch(`/api/public/book/${slug}`);
        if (!resp.ok) throw new Error("not found");
        const data = await resp.json() as { meetingType: MeetingTypePublic; slots: Slot[] };
        setMeetingType(data.meetingType);
        setSlots(data.slots);
      } catch {
        setError("This booking page could not be found or is no longer active.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  const grouped = groupSlotsByDate(slots);
  const availableDates = Object.keys(grouped).sort();
  const visibleDates = availableDates.slice(dateOffset, dateOffset + 7);

  const handleConfirm = async () => {
    if (!selectedSlot || !guestName || !guestEmail) return;
    setSubmitting(true);
    try {
      const resp = await apiFetch(`/api/public/book/${slug}/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          guestName,
          guestEmail,
          guestPhone: guestPhone || undefined,
          startTime: selectedSlot.start,
          notes: notes || undefined,
        }),
      });
      if (!resp.ok) throw new Error("Booking failed");
      const data = await resp.json() as { meetingLink: string };
      setMeetingLink(data.meetingLink);
      setStep("confirmed");
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error || !meetingType) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Page not found</h2>
          <p className="text-gray-500">{error ?? "This booking link is no longer active."}</p>
        </div>
      </div>
    );
  }

  const accentColor = meetingType.color ?? "#6366f1";

  if (step === "confirmed") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-10 text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: accentColor + "20" }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: accentColor }} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">You're booked!</h2>
          <p className="text-gray-500 mb-6">A confirmation has been sent to <span className="font-medium text-gray-700">{guestEmail}</span></p>
          <div className="bg-slate-50 rounded-2xl p-5 text-left space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-700">{selectedDate ? formatDate(selectedDate) : ""}</span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-700">{selectedSlot ? `${formatTime(selectedSlot.start)} · ${meetingType.duration} min` : ""}</span>
            </div>
            {meetingLink && (
              <div className="flex items-center gap-3">
                <Video className="w-4 h-4 text-gray-400 shrink-0" />
                <a href={meetingLink} target="_blank" rel="noopener noreferrer" className="text-sm underline break-all" style={{ color: accentColor }}>
                  {meetingLink}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title={meetingType ? `Book a Meeting with ${meetingType.title} — LaunchTactix` : "Book a Meeting — LaunchTactix"}
        description={meetingType?.description ?? "Schedule a meeting using LaunchTactix's smart booking system."}
        canonical={`https://launchtactix.com/book/${slug}`}
      />
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl max-w-3xl w-full overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Left panel: meeting info */}
          <div className="md:w-72 shrink-0 p-8 border-b md:border-b-0 md:border-r border-gray-100">
            <div className="w-10 h-10 rounded-xl mb-6 flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: accentColor }}>
              {meetingType.title[0]}
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">{meetingType.title}</h1>
            {meetingType.description && (
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">{meetingType.description}</p>
            )}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>{meetingType.duration} minutes</span>
              </div>
              {meetingType.location && (
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{meetingType.location}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Video className="w-4 h-4 text-gray-400" />
                <span>Google Meet link sent after booking</span>
              </div>
              {meetingType.price != null && meetingType.price > 0 && (
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span>${meetingType.price}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Globe className="w-4 h-4 text-gray-400" />
                <span>Your local time</span>
              </div>
            </div>

            {/* Selection summary */}
            {selectedDate && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Selected</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Calendar className="w-4 h-4" style={{ color: accentColor }} />
                    <span>{formatDate(selectedDate)}</span>
                  </div>
                  {selectedSlot && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Clock className="w-4 h-4" style={{ color: accentColor }} />
                      <span>{formatTime(selectedSlot.start)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right panel: date/time/form */}
          <div className="flex-1 p-8">
            {/* Date selection */}
            {step === "select-date" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Select a date</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setDateOffset(Math.max(0, dateOffset - 7))}
                      disabled={dateOffset === 0}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDateOffset(dateOffset + 7)}
                      disabled={dateOffset + 7 >= availableDates.length}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {availableDates.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No available slots found. Please check back later.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-7 gap-2">
                    {visibleDates.map((date) => {
                      const d = new Date(date + "T12:00:00");
                      const isSelected = selectedDate === date;
                      return (
                        <button
                          key={date}
                          onClick={() => { setSelectedDate(date); setStep("select-time"); }}
                          className="flex flex-col items-center p-3 rounded-2xl border-2 transition-all hover:shadow-sm"
                          style={isSelected ? { borderColor: accentColor, backgroundColor: accentColor + "10" } : { borderColor: "transparent", backgroundColor: "#f8fafc" }}
                        >
                          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                            {d.toLocaleDateString("en-US", { weekday: "short" })}
                          </span>
                          <span className="text-xl font-bold mt-0.5" style={isSelected ? { color: accentColor } : { color: "#1e293b" }}>
                            {d.getDate()}
                          </span>
                          <span className="text-[10px] text-gray-400 mt-0.5">
                            {grouped[date]?.length ?? 0} slots
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Time slot selection */}
            {step === "select-time" && selectedDate && (
              <div>
                <button onClick={() => { setStep("select-date"); setSelectedSlot(null); }} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 mb-6 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Select a time</h2>
                <p className="text-sm text-gray-500 mb-6">{formatDate(selectedDate)}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                  {(grouped[selectedDate] ?? []).map((slot) => {
                    const isSelected = selectedSlot?.start === slot.start;
                    return (
                      <button
                        key={slot.start}
                        onClick={() => { setSelectedSlot(slot); setStep("fill-form"); }}
                        className="py-2.5 px-4 rounded-xl border-2 text-sm font-medium transition-all hover:shadow-sm"
                        style={isSelected ? { borderColor: accentColor, color: accentColor, backgroundColor: accentColor + "10" } : { borderColor: "#e2e8f0", color: "#374151", backgroundColor: "#f8fafc" }}
                      >
                        {formatTime(slot.start)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Guest form */}
            {step === "fill-form" && selectedSlot && (
              <div>
                <button onClick={() => { setStep("select-time"); }} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 mb-6 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Your details</h2>
                <p className="text-sm text-gray-500 mb-6">
                  {formatDate(selectedDate!)} at {formatTime(selectedSlot.start)}
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Jane Smith"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                      style={{ ["--tw-ring-color" as string]: accentColor }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-red-400">*</span></label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="jane@company.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                    <input
                      type="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Anything you'd like to share before the meeting…"
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all resize-none"
                    />
                  </div>
                  <button
                    onClick={handleConfirm}
                    disabled={!guestName || !guestEmail || submitting}
                    className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: accentColor }}
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {submitting ? "Confirming…" : "Confirm booking"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export function BookingPageWrapper({ params }: { params: Record<string, string | undefined> }) {
  const slug = params.slug ?? "";
  if (!slug) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <p className="text-gray-500">Invalid booking link.</p>
    </div>
  );
  return <BookingPage slug={slug} />;
}
